import { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import DashboardLayout from '../../components/DashboardLayout';
import { Skeleton } from '../../components/Skeleton';
import SeverityBadge from '../../components/SeverityBadge';
import { EmptySignal } from '../../components/illustrations';
import { formatDateTime } from '../../lib/time';

const FILTERS = ['all', 'critical', 'warning', 'info'];

export default function AdminAlerts() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage;
  const [params] = useSearchParams();
  const customerId = params.get('customer') || '';
  const [severity, setSeverity] = useState('all');
  const [rows, setRows] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);

  const load = useCallback(async (before) => {
    const res = await api.adminAllAlerts({
      severity: severity === 'all' ? undefined : severity,
      customerId: customerId || undefined,
      before: before || undefined,
      limit: 60,
    });
    setNextBefore(res.nextBefore || null);
    setRows((prev) => (before ? [...prev, ...(res.alerts || [])] : res.alerts || []));
  }, [severity, customerId]);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  return (
    <DashboardLayout title={t('adminMon.alerts_title')} heading={false}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sp-5)', flexWrap: 'wrap', gap: 12 }}>
        <h1 className="page-title" style={{ margin: 0 }}>{t('adminMon.alerts_title')}</h1>
        <div className="lang-toggle">
          {FILTERS.map((f) => (
            <button key={f} className={severity === f ? 'active' : ''} onClick={() => setSeverity(f)}>
              {f === 'all' ? (lang === 'tr' ? 'Tümü' : 'All') : t(`severity.${f}`)}
            </button>
          ))}
        </div>
      </div>

      {customerId && (
        <div className="badge info" style={{ marginBottom: 'var(--sp-4)' }}>
          {t('adminMon.filtered_customer')} · <Link to="/dashboard/alerts">{t('adminMon.clear_filter')}</Link>
        </div>
      )}

      {loading ? (
        <div className="card">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="sk-text" style={{ margin: '14px 0' }} />)}</div>
      ) : rows.length === 0 ? (
        <div className="empty-state"><EmptySignal /><h3>{t('dashboard.no_alerts')}</h3></div>
      ) : (
        <>
          <div className="card" style={{ padding: 0 }}>
            <div className="list">
              {rows.map((a) => (
                <div className="list-row" key={a.id} style={{ padding: 'var(--sp-4)' }}>
                  <SeverityBadge severity={a.severity} />
                  <div className="row-main">
                    <div style={{ fontWeight: 500, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {t(`metric.${a.type}`, a.type)}
                      {a.customer && (
                        <Link to={`/admin/customers/${a.customer.id}`} style={{ fontSize: 12, color: 'var(--text-3)' }}>
                          · {a.customer.displayName || a.customer.email}
                        </Link>
                      )}
                      {a.serverLabel && <span style={{ fontSize: 12, color: 'var(--text-3)' }}>· {a.serverLabel}</span>}
                    </div>
                    <div style={{ color: 'var(--text-3)', fontSize: 12.5 }}>{a.message}</div>
                  </div>
                  <div className="row-time">{formatDateTime(a.triggeredAt, lang)}</div>
                </div>
              ))}
            </div>
          </div>
          {nextBefore && (
            <div style={{ textAlign: 'center', marginTop: 'var(--sp-4)' }}>
              <button className="btn btn-subtle btn-sm" disabled={more}
                onClick={async () => { setMore(true); await load(nextBefore); setMore(false); }}>
                {more ? t('common.loading') : t('adminMon.load_more')}
              </button>
            </div>
          )}
        </>
      )}
    </DashboardLayout>
  );
}
